import React from 'react';
import { motion, AnimatePresence as AnimatePresenceComponent } from 'framer-motion';
import { Check } from 'lucide-react';
import { FlattenedProductProps } from '../../../models/index';
import { useCategory } from '../../../hooks/useCategory';
import ModalV1 from '../modal-v1/index';

const AnimatePresence = AnimatePresenceComponent as React.FC<any>;

type CategoryListProps = {
	data: FlattenedProductProps[];
	show: boolean;
	setShow: (v: boolean) => void;
	/** Controlled: array of selected category keys */
	selectedCategories: string[];
	/** Fires whenever selection changes. Parent filters with OR logic. */
	onSelectionChange: (selected: string[]) => void;
	setSearchValue: (v: string) => void;
};

const listVariant = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.04 } },
};

const itemVariant = {
	hidden: { opacity: 0, x: -8 },
	visible: { opacity: 1, x: 0, transition: { duration: 0.2 } },
};

// ─── Component ────────────────────────────────────────────────────────────────

const CategoryListMobile = ({
	data,
	show,
	setShow,
	selectedCategories,
	onSelectionChange,
	setSearchValue,
}: CategoryListProps) => {
	const categories = useCategory(data);
	// Exclude "all" from entries since it's derived, not a real category
	const { all: totalItems = 0, ...realCategories } = (categories as Record<string, number>) || {};
	const entries = Object.entries(realCategories) as [string, number][];

	// "All" is selected only if it's in the selectedCategories array (special marker)
	const isAllSelected = selectedCategories.includes('__all__');

	const toggleCategory = (key: string) => {
		if (key === 'All') {
			// User clicked "All" — show everything by setting special marker
			onSelectionChange(['__all__']);
			setSearchValue('');
			return;
		}

		// User clicked a specific category — remove the "All" marker if present
		let next = selectedCategories.filter((k) => k !== '__all__');

		const already = next.includes(key);
		if (already) {
			next = next.filter((k) => k !== key);
		} else {
			next = [...next, key];
		}

		onSelectionChange(next);
		setSearchValue('');
	};

	const handleClearAll = () => {
		onSelectionChange([]);
		setSearchValue('');
	};

	const allItemsCount = totalItems || entries.reduce((sum, [, v]) => sum + v, 0);

	return (
		<ModalV1
			classes={{ content: 'z-[500]' }}
			show={show}
			onClose={() => setShow(false)}
			headerAction={
				<button
					onClick={handleClearAll}
					className="text-sm font-medium text-gray-900 hover:text-gray-500 transition-colors"
				>
					Clear All
				</button>
			}
		>
			{!entries.length ? (
				<p className="px-4 py-3 text-center text-sm text-gray-400">No available categories</p>
			) : (
				<motion.ul variants={listVariant} initial="hidden" animate="visible" className="divide-y divide-gray-100">
					{/* ── "All" row ── */}
					<CategoryRow
						label="All"
						count={allItemsCount}
						checked={isAllSelected}
						onToggle={() => toggleCategory('All')}
					/>

					{/* ── Per-category rows ── */}
					{entries.map(([key, count]) => (
						<CategoryRow
							key={key}
							label={key}
							count={count}
							checked={selectedCategories.includes(key)}
							onToggle={() => toggleCategory(key)}
						/>
					))}
				</motion.ul>
			)}
		</ModalV1>
	);
};

type CategoryRowProps = {
	label: string;
	count: number;
	checked: boolean;
	onToggle: () => void;
};

const CategoryRow = ({ label, count, checked, onToggle }: CategoryRowProps) => (
	<motion.li variants={itemVariant}>
		<button
			onClick={onToggle}
			className="w-full flex items-center justify-between px-5 py-5 hover:bg-gray-50 transition-colors text-left"
		>
			{/* Left side: checkbox + label */}
			<div className="flex items-center gap-3 flex-1">
				{/* Checkbox */}
				<span
					className={`
          flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
          ${checked ? 'bg-[#1e1b2e] border-[#1e1b2e]' : 'border-gray-300 bg-white'}
        `}
				>
					<AnimatePresence>
						{checked && (
							<motion.span
								key="check"
								initial={{ scale: 0, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								exit={{ scale: 0, opacity: 0 }}
								transition={{ duration: 0.15 }}
							>
								<Check size={12} strokeWidth={3} className="text-white" />
							</motion.span>
						)}
					</AnimatePresence>
				</span>

				{/* Label */}
				<span className="text-sm font-medium text-gray-800">{label}</span>
			</div>

			{/* Item count - right side */}
			<span className="flex-shrink-0 text-sm text-gray-400 ml-3">
				{count} Item{count !== 1 ? 's' : ''}
			</span>
		</button>
	</motion.li>
);

export default CategoryListMobile;
